import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import {
  File,
  Mutation,
  MutationDeleteContactAttachmentArgs,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { LggOptionsDropdownButtonWithCustomTrigger } from 'src/components/general/button/dropdown-button';
import { OptionsBottomDrawer } from 'src/components/general/drawer/bottom/options-bottom-drawer';
import { useShowConfirmationModal } from 'src/components/general/feedback/hooks/use-show-confirmation-modal';
import { useShowNotification } from 'src/components/general/feedback/hooks/use-show-notification';
import { Icon } from 'src/components/general/icon';
import { MoreOptionsIcon } from 'src/components/pages/conversations/components/contact-interactions/shared';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';
import { useVisible } from 'src/hooks/use-visible';
import { downloadFile } from 'src/utils/download-file';
import { DELETE_ATTACHMENT } from './queries';

const StyledIcon = styled(Icon)`
  & path {
    fill: ${({ theme }) => theme.colors.secondaryCoral};
  }
`;

const StyledMoreOptionsIcon = styled(MoreOptionsIcon)`
  path {
    fill: ${({ theme }) => theme.colors.geyser};
  }
`;

export const ContactAttachmentFileOptions: React.FC<{
  id: string;
  attachment: File;
  onDeleted: () => void;
}> = ({ id, attachment, onDeleted }) => {
  const { t } = useTranslation(['contacts']);
  const handleGraphQLError = useHandleGraphQLError();
  const showConfirmationModal = useShowConfirmationModal();
  const breakpointUpMd = useBreakpoint(up('md'));
  const currentInstitution = useCurrentInstitution();
  const showNotification = useShowNotification();

  const [deleteAttachment] = useMutation<
    Pick<Mutation, 'deleteContactAttachment'>,
    MutationDeleteContactAttachmentArgs
  >(DELETE_ATTACHMENT, {
    onError: (err) => {
      const code = err?.graphQLErrors?.[0]?.extensions?.code;

      if (code === 'FILE_VALIDATION:FILE_ALREADY_EXISTS_BY_OTHER') {
        showNotification({
          type: 'error',
          title: t('contacts:attachments.errors.fileDelete'),
          message: t(
            'contacts:attachments.errors.fileAlreadyExistsByOther.unauthorizedDelete',
          ),
        });
      } else {
        handleGraphQLError(err);
      }
    },
    onCompleted: () => {
      showNotification({
        type: 'success',
        title: t('contacts:attachments.deleteModal.success'),
      });
    },
  });

  const {
    visible: isMobileOptionsVisible,
    show: showMobileOptions,
    close: closeMobileOptions,
  } = useVisible();

  const moreOptionsVisibilityHandler = useVisible();

  const handleDelete = useCallback(async () => {
    showConfirmationModal({
      title: t('contacts:attachments.deleteModal.title'),
      message: t('contacts:attachments.deleteModal.body', {
        filename: attachment.filename,
      }),
      confirmButtonText: t('contacts:attachments.deleteModal.confirmButtonText'),
      confirmButtonType: 'delete',
      testId: 'delete-attachment-confirmation-modal',
      onConfirm: async () => {
        await deleteAttachment({
          variables: { id, institutionId: currentInstitution.id },
        });
        onDeleted();
      },
      width: 'unset',
    });
  }, [
    attachment.filename,
    currentInstitution.id,
    deleteAttachment,
    id,
    onDeleted,
    showConfirmationModal,
    t,
  ]);

  const handleDownload = useCallback(async () => {
    await downloadFile({
      fileUrl: attachment.url,
      fileName: attachment.filename,
    });
  }, [attachment.filename, attachment.url]);

  const options = [
    {
      onClick: handleDownload,
      label: t('contacts:attachments.download'),
      icon: 'download',
      'data-lgg-id': 'contact-attachment-download-option',
    },
    {
      onClick: handleDelete,
      label: t('contacts:attachments.delete'),
      icon: <StyledIcon type="deleteBin" />,
      'data-lgg-id': 'contact-attachment-delete-option',
    },
  ];

  if (breakpointUpMd) {
    return (
      <LggOptionsDropdownButtonWithCustomTrigger
        visibilityHandler={moreOptionsVisibilityHandler}
        options={options}
        customDropdownProps={{
          placement: 'bottomRight',
          align: {
            offset: [8, 11],
          },
        }}
        customTrigger={
          <StyledMoreOptionsIcon
            type="moreOptions"
            lggTestId="contact-attachment-item-more-options-menu"
          />
        }
      />
    );
  }

  return (
    <>
      <StyledMoreOptionsIcon
        className="list-item-menu"
        type="moreOptions"
        onClick={showMobileOptions}
      />
      <OptionsBottomDrawer
        onClose={closeMobileOptions}
        title={t('contacts:attachments.fileOptions')}
        visible={!breakpointUpMd && isMobileOptionsVisible}
        options={options}
      />
    </>
  );
};
